/**
 * La landing del «Plano Google Maps 7 Dias» (diseño V4 del lienzo «Landing
 * Fundadores PT», con la primera pantalla personalizada de la V6 —
 * `HeroPersonalizado`). Una página, dos puertas:
 *
 *   - `/plano-google-maps`, con sesión: la abren el popup, la cápsula de
 *     cabecera o un enlace directo. Sin sesión, la app manda al login y vuelve.
 *   - `/oferta/<token>`, el enlace del correo pt: sin sesión, el token dice de
 *     quién es. Enseña lo mismo; la sesión solo se abre al pulsar «Começar o meu
 *     plano» (los escáneres de correo cargan los enlaces, nunca pulsan).
 *
 * Fuera de `pages/` y sin Layout a propósito: es una página de venta, no una
 * pantalla del panel, y todo lo que vive en `pages/` se auto-monta con el
 * Layout del panel.
 *
 * Todo el texto es portugués fijo (`copy.js`): la oferta es solo para Portugal.
 */
import { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, CheckCircle2, Eye, Loader2 } from 'lucide-react';
import posthog from 'posthog-js';
import toast from 'react-hot-toast';

import { CtaSinMedir } from '@/components/CtaLink';
import * as API_REAL from '@/api/ofertaPtService';
import { getOfertaPtLanding } from '@/api/ofertaPtService';
import useStripeRedirectToast from '@/hooks/useStripeRedirectToast';
import { useMsRestantes } from '@/hooks/useOfertaPt';

import BarraFixa from './BarraFixa';
import { LOCAIS_MAX } from './SeletorLocais';
import HeroPersonalizado from './HeroPersonalizado';
import {
  ComoFunciona, CtaFinal, Duvidas, FaixaQuemAparece, OfertaFooter, OfertaNav,
  ProvaSocial, Timeline, TudoOQueRecebes,
} from './Secoes';
import useComecarPlano from './useComecarPlano';

export const LANDING_KEY = ['oferta-pt', 'landing'];

function Aviso({ icone: Icone = AlertCircle, titulo, texto, children }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-[#fffaf0] px-4 py-10 font-sans text-[#1d1d1f]">
      <div className="w-full max-w-md rounded-3xl border border-[#ececf0] bg-white p-7 text-center shadow-sm">
        <Icone className="mx-auto h-12 w-12 text-[#feae2c]" aria-hidden="true" />
        <h1 className="mt-4 text-xl font-extrabold">{titulo}</h1>
        {texto && <p className="mt-2 text-sm leading-relaxed text-[#4a4a52]">{texto}</p>}
        {children}
      </div>
    </div>
  );
}

const BOTAO = 'mt-6 inline-flex min-h-[48px] items-center justify-center rounded-xl bg-[#feae2c] px-6 font-bold text-[#1d1d1f] no-underline hover:bg-[#f59e0b]';

export default function OfertaPtV4Page() {
  const { token } = useParams();
  const { comecar, cargando } = useComecarPlano(token);
  // De vuelta de Stripe (`?stripe=success`): el aviso y el reconcile de
  // siempre. Se lee ANTES de que el hook limpie la URL, para enseñar el gracias.
  const [volvioDePagar] = useState(
    () => typeof window !== 'undefined'
      && new URLSearchParams(window.location.search).get('stripe') === 'success',
  );
  useStripeRedirectToast();

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: [...LANDING_KEY, token || 'sessao'],
    queryFn: () => getOfertaPtLanding(token),
    retry: false,
    // Con GMB, si el mapa de su zona se está calculando, se vuelve a preguntar
    // hasta que salgan los números.
    refetchInterval: (q) => (q.state.data?.diagnostico?.estado === 'a_calcular' ? 5000 : false),
  });

  // El webhook de Stripe tarda unos segundos: se vuelve a leer para que la
  // plaza ya cuente y la página pase a «já tens o plano».
  useEffect(() => {
    if (!volvioDePagar) return undefined;
    const id = setTimeout(() => refetch(), 3000);
    return () => clearTimeout(id);
  }, [volvioDePagar, refetch]);

  // Quien llega por el correo no tiene sesión y PostHog lo vería anónimo: el
  // token dice quién es, así que se identifica con el mismo id que el login.
  // Solo si el navegador sigue anónimo: si ya hay alguien identificado (un
  // admin que abre el enlace de un cliente, un correo reenviado a un socio
  // con cuenta) no se le pisa la identidad.
  const posthogId = token ? data?.posthogId : null;
  useEffect(() => {
    if (!posthogId || !posthog.__loaded) return;
    if (posthog.get_property('$user_state') === 'identified') return;
    posthog.identify(posthogId);
  }, [posthogId]);

  const oferta = data?.oferta;
  const msRestantes = useMsRestantes(oferta?.cierreAt);
  // La prueba a una cuenta admin (Admin → Campaña pt): la landing del cliente,
  // con sus datos, sin entrar ni comprar. Ver `datos_landing` en el backend.
  const soloVer = !!(token && data?.soloVer);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white">
        <Loader2 className="h-8 w-8 animate-spin text-[#feae2c]" aria-hidden="true" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <Aviso titulo="Este link não é válido" texto="Entra na tua conta para ver o Plano Google Maps 7 Dias.">
        <Link to="/login?redirect=/plano-google-maps" className={BOTAO}>Entrar na minha conta</Link>
      </Aviso>
    );
  }

  if (token && !data.puedeEntrar && !soloVer) {
    const caducado = data.motivo === 'caducado';
    return (
      <Aviso
        titulo={caducado ? 'Este link expirou' : 'Não conseguimos abrir a tua conta com este link'}
        texto="Entra com o teu email e palavra-passe para ver o Plano Google Maps 7 Dias."
      >
        <Link to="/login?redirect=/plano-google-maps" className={BOTAO}>Entrar na minha conta</Link>
      </Aviso>
    );
  }

  if (volvioDePagar || oferta?.usada) {
    return (
      <Aviso
        icone={CheckCircle2}
        titulo="Obrigado! O teu Plano Google Maps 7 Dias já está em marcha."
        texto="Vais receber um email com a fatura e o formulário de 3 minutos. A partir daí, recebes um email em cada passo."
      >
        <a href="/MyBusiness" className={BOTAO}>Ir para o meu painel</a>
      </Aviso>
    );
  }

  if (!oferta?.elegible && !soloVer) {
    // Con sesión, quien no es de la oferta (ya Business, otra región) vuelve a
    // su panel; con el enlace del correo no hay panel al que volver sin entrar.
    if (!token) return <Navigate to="/MyBusiness" replace />;
    return (
      <Aviso titulo="Esta oferta não está disponível para a tua conta" texto="Se já és cliente Business, tens tudo no teu painel.">
        <Link to="/login" className={BOTAO}>Entrar na minha conta</Link>
      </Aviso>
    );
  }

  if (!oferta.abierta || msRestantes <= 0) {
    return (
      <Aviso titulo="O Plano Google Maps 7 Dias já terminou" texto="Obrigado pelo interesse. Fala connosco se quiseres saber o que podemos fazer pela tua ficha.">
        <a href="/MyBusiness" className={BOTAO}>Ir para o meu painel</a>
      </Aviso>
    );
  }

  const vista = (
    <OfertaPtV4Vista
      data={data}
      comecar={soloVer
        ? () => toast('Vista de admin: este link não abre sessão nem paga.')
        : comecar}
      cargando={cargando}
      token={token} landingKey={[...LANDING_KEY, token || 'sessao']}
    />
  );
  if (soloVer) {
    return (
      <CtaSinMedir>
        <div className="sticky top-0 z-50 flex items-center justify-center gap-2 bg-[#1d1d1f] px-4 py-2 text-center text-xs font-semibold text-white">
          <Eye className="h-4 w-4 shrink-0" aria-hidden="true" />
          Vista de admin · é o que vê o cliente, com os teus dados. Não abre sessão nem paga.
        </div>
        {vista}
      </CtaSinMedir>
    );
  }
  // El enlace de una prueba de Admin → Campaña pt: se ve igual, pero sus CTAs
  // no cuentan (quien la abre es del equipo).
  return data.prueba ? <CtaSinMedir>{vista}</CtaSinMedir> : vista;
}

/**
 * La landing ya con sus datos: lo que pintan las dos puertas de arriba y la
 * página de capturas (`pages/capturas/PlanoGoogleMapsCaptura.jsx`), que la
 * enseña con datos inventados para revisarla sin cuenta.
 *
 * @param {{ data: ReturnType<typeof import('@/api/ofertaPtService').mapLanding>,
 *           comecar: (id: string) => void, cargando: string|null,
 *           token?: string, landingKey: unknown[], api?: object }} props
 *   `api`: solo la página de capturas, que simula el diagnóstico sin backend.
 */
export function OfertaPtV4Vista({ data, comecar: comecarBase, cargando, token, landingKey, api = API_REAL }) {
  const { oferta } = data;
  const semGmb = !data.gmbConectado;
  const queryClient = useQueryClient();
  // La tarjeta del negocio cuesta una llamada a Google la primera vez, y el GET
  // de la landing no la hace (lo abren también los escáneres de correo): si
  // falta, la pide la página, una vez, y la mete en lo que ya pintó.
  const [fichaACarregar, setFichaACarregar] = useState(!!data.fichaPendente);
  const pedida = useRef(false);
  useEffect(() => {
    if (!data.fichaPendente || !api.pedirFicha) {
      // Resuelta: si vuelve a faltar (eligió otro negocio en el buscador), se pide otra vez.
      pedida.current = false;
      setFichaACarregar(false);
      return;
    }
    if (pedida.current) return;
    pedida.current = true;
    setFichaACarregar(true);
    api.pedirFicha(token)
      .then(({ ficha, sugestoes }) => {
        queryClient.setQueryData(landingKey, (prev) => (prev
          ? { ...prev, ficha, sugestoes, fichaPendente: false } : prev));
      })
      .catch(() => { /* sin ficha: la landing enseña el buscador */ })
      .finally(() => setFichaACarregar(false));
  }, [data.fichaPendente, api, token, landingKey, queryClient]);
  // Los locales del precio (héroe, tarjeta y barra fija), UN solo estado para
  // los tres. Los elige siempre él, de 1 a 30 (feedback de producto, 29/10):
  // empieza en sus locales activos (`checkout.locais`, 1 si no tiene) y todos
  // los «Começar» compran los que marque.
  const locaisIniciales = Math.min(LOCAIS_MAX, Math.max(1, data.locais || 1));
  const [locais, setLocais] = useState(locaisIniciales);
  // Si cambian (la página de capturas cambia de variante, o activa un local y
  // la landing se recarga), se vuelve a partir de los suyos.
  useEffect(() => { setLocais(locaisIniciales); }, [locaisIniciales]);
  const seletor = { locais, setLocais };
  const comecar = (id) => comecarBase(id, locais);
  const vagas = { restantes: oferta.plazasRestantes, total: oferta.plazasTotal || 50 };

  return (
    <div
      className="min-h-screen bg-white font-sans text-[#1d1d1f] antialiased"
      // Con el selector la barra ocupa dos filas por debajo de 640 px.
      style={{ paddingBottom: 'calc(156px + env(safe-area-inset-bottom))' }}
    >
      <OfertaNav {...vagas} comecar={comecar} cargando={cargando} />
      <main>
        <HeroPersonalizado
          data={data} vagas={vagas} comecar={comecar} cargando={cargando} seletor={seletor}
          token={token} landingKey={landingKey} api={api} fichaACarregar={fichaACarregar}
        />
        <FaixaQuemAparece />
        <ComoFunciona />
        <TudoOQueRecebes comecar={comecar} cargando={cargando} seletor={seletor} />
        <ProvaSocial />
        <Timeline semGmb={semGmb} />
        <Duvidas semGmb={semGmb} />
        <CtaFinal restantes={vagas.restantes} cierreAt={oferta.cierreAt} comecar={comecar} cargando={cargando} />
      </main>
      <OfertaFooter />
      <BarraFixa {...vagas} comecar={comecar} cargando={cargando} seletor={seletor} />
    </div>
  );
}
